/**
 * The month filter — a dropdown, not a slider.
 *
 * It lives in the Expenses page header, beside the title, because that is what
 * it is: the filter the whole section is scoped by. Add, Journal and Budget all
 * answer questions about one month, and this is where you say which.
 *
 * A `<select>` rather than `<input type="month">` on purpose. Native month
 * inputs are unevenly supported and degrade to a bare text box where they are
 * not, which would be a worse control than the one being replaced; a select
 * renders predictably everywhere and, on a phone, opens the OS wheel picker —
 * the nicest month picker either platform has.
 *
 * The range is bounded rather than infinite because an unbounded list of months
 * is a list of mostly empty ones. The month currently selected is always
 * included, so a value from anywhere cannot fall out of its own control.
 */
import { useMemo } from 'react';
import { currentMonth, monthLabel, shiftMonth } from './months.js';

interface Props {
  month: string;
  onChange: (month: string) => void;
}

/** How far the dropdown reaches. Two years back covers year-on-year comparison. */
const MONTHS_BACK = 24;
const MONTHS_AHEAD = 3;

export function MonthFilter({ month, onChange }: Props) {
  const now = currentMonth();
  const isCurrent = month === now;

  const options = useMemo(() => {
    const months = new Set<string>();
    for (let i = MONTHS_AHEAD; i >= -MONTHS_BACK; i -= 1) months.add(shiftMonth(now, i));
    // Whatever is selected belongs in the list even if it sits outside the
    // window — a select whose value is not among its options renders blank.
    months.add(month);
    return [...months].sort((a, b) => b.localeCompare(a));
  }, [now, month]);

  return (
    <div className="month-filter">
      {/* No visible label: the select already says "September 2026", so a word
          above it spends a line saying nothing. The accessible name stays. */}
      <select value={month} onChange={(e) => onChange(e.target.value)} aria-label="Month">
        {options.map((value) => (
          <option key={value} value={value}>
            {monthLabel(value)}
            {value === now ? ' · this month' : ''}
          </option>
        ))}
      </select>

      {!isCurrent && (
        <button type="button" className="month-today" onClick={() => onChange(now)}>
          This month
        </button>
      )}
    </div>
  );
}
